import { useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { SectionHeading } from '@/components/ui/SectionHeading'
import { WebCalculator } from './WebCalculator'
import { ExistingSiteCalculator } from './ExistingSiteCalculator'
import { GraphicDesignPanel } from './GraphicDesignPanel'

type Tab = 'web' | 'webdesign' | 'grafika'

/**
 * #usluge — Phase 3. Three tabs matching the live site's real behavior
 * exactly (`data-stab="web|webdesign|grafika"` panel switch): a new-website
 * calculator with bundle savings, a flat existing-site calculator, and a
 * graphic-design multi-select. Card/row visual language follows Stitch's
 * `services_pricing` mockup (glass cards, pill tabs, cyan selected state);
 * the tab structure itself is real-site behavior, not in the static Stitch
 * mockup (which shows everything on one screen for visual reference only).
 */
export function ServicesCalculator() {
  const { t } = useLanguage()
  const [tab, setTab] = useState<Tab>('web')

  const tabs: { id: Tab; labelKey: 'tab-web' | 'tab-webdesign' | 'tab-grafika' }[] = [
    { id: 'web', labelKey: 'tab-web' },
    { id: 'webdesign', labelKey: 'tab-webdesign' },
    { id: 'grafika', labelKey: 'tab-grafika' },
  ]

  return (
    <section id="usluge" className="px-6 py-section-gap md:px-margin-desktop">
      <div className="mx-auto flex max-w-container flex-col gap-10">
        <SectionHeading title={t('services-title')} subtitle={t('services-sub')} />

        <div
          role="tablist"
          aria-label={t('services-title')}
          className="mx-auto flex flex-wrap justify-center gap-2 rounded-pill border border-white/10 bg-white/[0.03] p-1.5"
        >
          {tabs.map((tabDef) => (
            <button
              key={tabDef.id}
              type="button"
              role="tab"
              id={`tab-${tabDef.id}`}
              aria-selected={tab === tabDef.id}
              aria-controls={`tabpanel-${tabDef.id}`}
              tabIndex={tab === tabDef.id ? 0 : -1}
              onClick={() => setTab(tabDef.id)}
              className={`relative rounded-pill px-5 py-2 text-sm font-semibold transition-all duration-normal ${
                tab === tabDef.id
                  ? 'glow-ring bg-accent-fire/15 text-accent-fire'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              {t(tabDef.labelKey)}
            </button>
          ))}
        </div>

        {tab === 'web' && (
          <div role="tabpanel" id="tabpanel-web" aria-labelledby="tab-web">
            <WebCalculator />
          </div>
        )}
        {tab === 'webdesign' && (
          <div role="tabpanel" id="tabpanel-webdesign" aria-labelledby="tab-webdesign">
            <ExistingSiteCalculator />
          </div>
        )}
        {tab === 'grafika' && (
          <div role="tabpanel" id="tabpanel-grafika" aria-labelledby="tab-grafika">
            <GraphicDesignPanel />
          </div>
        )}
      </div>
    </section>
  )
}
